import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileCheck, UserCheck, UserX, Clock, Users, GraduationCap,
  Mail, Phone, Calendar, Building2, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RegistrationsPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [pending, setPending] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fr = language === 'fr';

  useEffect(() => { loadStudents(); }, []);

  const loadStudents = async () => {
    try {
      const [pendingRes, rejectedRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/pending-students?status_filter=pending_validation`),
        axios.get(`${API_URL}/api/admin/pending-students?status_filter=rejected`),
      ]);
      setPending(pendingRes.data);
      setRejected(rejectedRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAction = async (userId, action) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API_URL}/api/admin/validate-student/${userId}?action=${action}`);
      toast.success(action === 'approve'
        ? (fr ? 'Etudiant valide et active' : 'Student approved and activated')
        : (fr ? 'Inscription rejetee' : 'Registration rejected'));
      loadStudents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setActionLoading(null); }
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-lg" />)}</div>;

  const StudentCard = ({ student, showActions }) => (
    <Card data-testid={`student-card-${student.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-full bg-[#0F2C59] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
              {(student.first_name?.[0] || '').toUpperCase()}{(student.last_name?.[0] || '').toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-slate-900">{student.first_name} {student.last_name}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{student.email}</span>
                {student.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{student.phone}</span>}
              </div>
              {student.student_record && (
                <div className="flex gap-2 mt-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px] py-0">
                    <GraduationCap className="w-3 h-3 mr-1" />{student.student_record.matricule}
                  </Badge>
                  {student.student_record.promotion_id && (
                    <Badge variant="secondary" className="text-[10px] py-0">
                      <BookOpen className="w-3 h-3 mr-1" />{student.student_record.program_id?.slice(0, 8)}
                    </Badge>
                  )}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                {new Date(student.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          {showActions && (
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                onClick={() => handleAction(student.id, 'approve')}
                disabled={actionLoading === student.id}
                data-testid={`approve-${student.id}`}
              >
                <UserCheck className="w-3.5 h-3.5" /> {fr ? 'Valider' : 'Approve'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1 text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs"
                onClick={() => handleAction(student.id, 'reject')}
                disabled={actionLoading === student.id}
                data-testid={`reject-${student.id}`}
              >
                <UserX className="w-3.5 h-3.5" /> {fr ? 'Rejeter' : 'Reject'}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="registrations-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="registrations-title">
          {fr ? 'Gestion des Inscriptions' : 'Registration Management'}
        </h1>
        <p className="text-sm text-slate-500">
          {fr ? 'Validez ou rejetez les inscriptions en attente' : 'Approve or reject pending registrations'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{pending.length}</p>
              <p className="text-xs text-slate-500">{fr ? 'En attente' : 'Pending'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <UserX className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{rejected.length}</p>
              <p className="text-xs text-slate-500">{fr ? 'Rejetes' : 'Rejected'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" className="gap-1.5" data-testid="tab-pending">
            <Clock className="w-4 h-4" />{fr ? 'En attente' : 'Pending'} ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="gap-1.5" data-testid="tab-rejected">
            <UserX className="w-4 h-4" />{fr ? 'Rejetes' : 'Rejected'} ({rejected.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4 space-y-3">
          {pending.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <FileCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">{fr ? 'Aucune inscription en attente' : 'No pending registrations'}</p>
              </CardContent>
            </Card>
          ) : (
            pending.map(s => <StudentCard key={s.id} student={s} showActions={true} />)
          )}
        </TabsContent>

        <TabsContent value="rejected" className="mt-4 space-y-3">
          {rejected.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                {fr ? 'Aucune inscription rejetee' : 'No rejected registrations'}
              </CardContent>
            </Card>
          ) : (
            rejected.map(s => <StudentCard key={s.id} student={s} showActions={false} />)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RegistrationsPage;
